import * as React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import {
    SPRAY_EVENT_WIZARD_SCHEDULE_STEP,
    SPRAY_EVENT_WIZARD_APPLICATION_STEP,
    SPRAY_EVENT_WIZARD_EQUIPMENT_STEP,
    SPRAY_EVENT_WIZARD_PRODUCT_STEP,
    SPRAY_EVENT_WIZARD_SUMMARY_STEP
} from 'services/sprayEventWizard/constants';

// Global Components
import ErrorAlert from 'components/ErrorAlert';
import { Modal } from 'semantic-ui-react';

import { AppState } from 'services/types';
// Local Components
import { Step } from 'components/StepProgress';
import Wizard from 'components/Wizard';
import { SprayEventUuid } from 'services/sprayEvents/types';
import ProductPage from './components/ProductPage';
import ApplicationPage from './components/ApplicationPage';
import EquipmentPage from './components/EquipmentPage';
import SummaryPage from './components/SummaryPage';
import ReadOnlySummary from './components/ReadOnlySummary';
import {
    makeSelectInitialValues,
    makeSelectSprayEventIsCompleted
} from './selectors';

import SchedulePage from './components/SchedulePage';

export interface OwnProps {
    handleSubmit: any;
    sprayEventUuid?: SprayEventUuid;
    namespace: Namespace;
    title: string;
    isEditing?: boolean;
}

interface ConnectedProps {
    initialValues: any;
    sprayEventIsCompleted: boolean;
}

type Props = OwnProps & ConnectedProps;

interface State {
    lastSubmittedAt?: Date;
}

export const SPRAY_EVENT_CREATE = 'SPRAY_EVENT_CREATE';
export const SPRAY_EVENT_UPDATE = 'SPRAY_EVENT_UPDATE';

export type Namespace = typeof SPRAY_EVENT_CREATE | typeof SPRAY_EVENT_UPDATE;

const steps: Step[] = [
    {
        description: 'Schedule',
        value: SPRAY_EVENT_WIZARD_SCHEDULE_STEP
    },
    {
        description: 'App.',
        value: SPRAY_EVENT_WIZARD_APPLICATION_STEP
    },
    {
        description: 'Equip.',
        value: SPRAY_EVENT_WIZARD_EQUIPMENT_STEP
    },
    {
        description: 'Product',
        value: SPRAY_EVENT_WIZARD_PRODUCT_STEP
    },
    {
        description: 'Summary',
        value: SPRAY_EVENT_WIZARD_SUMMARY_STEP
    }
];

export class SprayEventFormComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { lastSubmittedAt: undefined };
    }

    render(): React.ReactElement {
        const {
            handleSubmit,
            initialValues,
            namespace,
            sprayEventIsCompleted,
            title,
            isEditing = false
        } = this.props;

        if (sprayEventIsCompleted && isEditing) {
            return <ReadOnlySummary initialValues={initialValues} />;
        }

        const setLastSubmittedAt = () => {
            this.setState({ lastSubmittedAt: new Date() });
        };

        return (
            <React.Fragment>
                <Modal.Header>{title}</Modal.Header>
                <Modal.Content>
                    <Wizard
                        onSubmit={handleSubmit}
                        steps={steps}
                        classname="spray-event-modal || constrain-width"
                        initialValues={initialValues}
                        namespace={namespace}
                        setLastSubmittedAt={setLastSubmittedAt}
                    >
                        <Wizard.Page>
                            <SchedulePage isEditing={isEditing} />
                            <ErrorAlert namespace={namespace} />
                        </Wizard.Page>
                        <Wizard.Page>
                            <ApplicationPage
                                lastSubmittedAt={this.state.lastSubmittedAt}
                            />
                            <ErrorAlert namespace={namespace} />
                        </Wizard.Page>
                        <Wizard.Page>
                            <EquipmentPage
                                lastSubmittedAt={this.state.lastSubmittedAt}
                            />
                            <ErrorAlert namespace={namespace} />
                        </Wizard.Page>
                        <Wizard.Page>
                            <ProductPage />
                            <ErrorAlert namespace={namespace} />
                        </Wizard.Page>
                        <Wizard.Page>
                            <SummaryPage />
                            <ErrorAlert namespace={namespace} />
                        </Wizard.Page>
                    </Wizard>
                </Modal.Content>
            </React.Fragment>
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    initialValues: makeSelectInitialValues(),
    sprayEventIsCompleted: makeSelectSprayEventIsCompleted()
});

export default connect(mapStateToProps)(SprayEventFormComponent);
